import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { supabase } from './supabaseClient';

const HEADER_BG = '#1e40af';
const HEADER_BORDER = '#60A5FA';
const PAGE_BG = '#F8FAFF';
const CARD_BORDER = '#60A5FA';
const CARD_HEADER_BG = '#dbeafe';

export default function LoginPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const isLogin = mode === 'login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        navigate('/app/');
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) throw error;

        setMessage('Account created. You can now log in.');
        setMode('login');
      }
    } catch (err) {
      setMessage(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: PAGE_BG }}>
      {/* Header */}
      <Box
        sx={{
          height: 72,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: HEADER_BG,
          color: 'white',
          borderBottom: `1px solid ${HEADER_BORDER}`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <img src="/ssiconfull.png" alt="logo" style={{ width:'160px'}} />
        </Stack>

        <Button
          variant="outlined"
          startIcon={<GroupsOutlinedIcon />}
          onClick={() => navigate('/app/participant')}
          sx={{
            color: 'white',
            borderColor: 'rgba(255,255,255,0.35)',
            textTransform: 'none',
            bgcolor: 'rgba(255,255,255,0.06)',
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.5)',
              bgcolor: 'rgba(255,255,255,0.12)',
            },
          }}
        >
          Participant mode
        </Button>
      </Box>

      {/* Main */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 460,
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'white',
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              bgcolor: CARD_HEADER_BG,
              borderBottom: `1px solid ${CARD_BORDER}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#1e3a8a' }}>
              {isLogin ? (
                <LoginOutlinedIcon sx={{ fontSize: 30 }} />
              ) : (
                <PersonAddAlt1OutlinedIcon sx={{ fontSize: 30 }} />
              )}
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {isLogin ? 'Facilitator login' : 'Create facilitator account'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isLogin
                  ? 'Sign in to access the facilitator dashboard and scenario tools.'
                  : 'Create an account to manage patients, scenarios, and live sessions.'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.25}>
                <TextField
                  type="email"
                  label="Email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                />

                <TextField
                  type="password"
                  label="Password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                />

                {message && (
                  <Alert severity={message.toLowerCase().includes('created') ? 'success' : 'error'}>
                    {message}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  endIcon={loading ? null : <ArrowForwardIcon />}
                  sx={{
                    textTransform: 'none',
                    height: 48,
                    fontWeight: 600,
                  }}
                >
                  {loading ? 'Please wait…' : isLogin ? 'Log in' : 'Create account'}
                </Button>

                <Box sx={{ pt: 0.5 }}>
                  <Button
                    type="button"
                    variant="text"
                    onClick={() => {
                      setMode(isLogin ? 'signup' : 'login');
                      setMessage('');
                      navigate(isLogin ? '/signup' : ' ');
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    {isLogin ? 'Need an account? Sign up' : 'Already have an account? Log in'}
                  </Button>
                </Box>
              </Stack>
            </form>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}